import { nextTick } from 'vue'
import { defineStore } from 'pinia'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'
import { useGroupStore } from '@/stores/useGroupStore'
import { useThemeStore } from '@/stores/useThemeStore'
import { useBroadcastStore } from '@/stores/useBroadcastStore'

import utils from '@/utils.js'
import consts from '@/consts.js'
import cache from '@/cache.js'

import debounce from 'lodash-es/debounce'
import uniq from 'lodash-es/uniq'
import { nanoid } from 'nanoid'

let sessionQueue = []
let sendingQueuePromise = Promise.resolve()
const nonCriticalOperationErrorStatusCodes = [400, 401, 404]
const maxOperationRetryCount = 3
const maxSendQueueRetryDelay = 5 * 60 * 1000 // 5 minutes
let sendQueueFailureCount = 0
let sendQueueRetryAt = 0
let sendQueueRetryTimer
const cardNameUpdatedAtByCardId = {}
const sendQueueDebounced = debounce((apiStore) => apiStore.trySendQueue(), 500)

const restoreSessionQueueFromBackup = async () => {
  sessionQueue = await cache.queueBackup()
  if (sessionQueue.length) {
    console.log('🛫 restored sessionQueue from backup', sessionQueue)
  }
}
restoreSessionQueueFromBackup()

const mergeOperationBody = (prev, next) => {
  const merged = { ...prev, ...next }
  // update name by most recent nameUpdatedAt
  const prevHasName = Object.hasOwn(prev, 'name')
  const nextHasName = Object.hasOwn(next, 'name')
  if (!prevHasName || !nextHasName) { return merged }
  let prevTime = 0
  let nextTime = 0
  if (prev.nameUpdatedAt) { prevTime = new Date(prev.nameUpdatedAt).getTime() }
  if (next.nameUpdatedAt) { nextTime = new Date(next.nameUpdatedAt).getTime() }
  const isPrevTimeMoreRecent = prevTime && prevTime >= nextTime
  if (isPrevTimeMoreRecent) {
    merged.name = prev.name
    merged.nameUpdatedAt = prev.nameUpdatedAt
  }
  return merged
}

// try to to merge new item into matching prev one
const addItemToQueue = (queue, newItem) => {
  const cumulativeDeltaOperations = ['updateUserCardsCreatedCount', 'updateUserCardsCreatedCountRaw']
  const shouldNotMergeOperations = ['createList', 'createCard', 'createBox', 'createConnection', 'createDrawingStroke', 'removeDrawingStroke', 'createUserNotification']
  let isPrevItem
  const newQueue = queue.map(prevItem => {
    const isOperationName = prevItem.name === newItem.name
    const isOperationDelta = cumulativeDeltaOperations.includes(newItem.name)
    const isItemId = prevItem.body.id === newItem.body.id
    const shouldIncrement = isOperationName && isOperationDelta
    const shouldMerge = isOperationName && isItemId && !shouldNotMergeOperations.includes(newItem.name)
    if (shouldIncrement) {
      newItem.body.delta = newItem.body.delta + prevItem.body.delta
      isPrevItem = true
      return newItem
    } else if (shouldMerge) {
      isPrevItem = true
      newItem.body = mergeOperationBody(prevItem.body, newItem.body) // { a: 1, b: 2 }, { a: 1, b: 3, c:2 } → { a: 1, b: 3, c:2 }
      return newItem
    } else {
      return prevItem
    }
  })
  // if no matching prev item, add new item to the queue
  if (!isPrevItem) {
    newQueue.push(newItem)
  }
  return newQueue
}

// put operations that failed to send back at the front of the queue, ahead of operations added while sending
const requeueOperations = async (operations) => {
  let queue = operations
  sessionQueue.forEach(item => {
    queue = addItemToQueue(queue, item)
  })
  sessionQueue = queue
  await cache.saveQueue(sessionQueue)
  await cache.saveQueueBackup(sessionQueue)
  console.warn('🛫 requeued operations', operations)
}

// Retry-After header seconds, from rate limited responses
const retryAfterDelay = (response) => {
  if (!response) { return 0 }
  const seconds = parseInt(response.headers?.get('Retry-After'))
  if (!seconds) { return 0 }
  return Math.min(seconds * 1000, maxSendQueueRetryDelay)
}

// wait longer after each failed send, so that failing requests don't overload the server
const updateSendQueueRetryDelay = (apiStore, response) => {
  sendQueueFailureCount += 1
  let delay = Math.pow(2, sendQueueFailureCount) * 1000
  delay = Math.min(delay, maxSendQueueRetryDelay)
  // random jitter, so that clients don't all retry at the same time
  delay = (delay / 2) + (Math.random() * delay / 2)
  delay = Math.max(delay, retryAfterDelay(response))
  sendQueueRetryAt = Date.now() + delay
  clearTimeout(sendQueueRetryTimer)
  sendQueueRetryTimer = setTimeout(() => apiStore.flushQueue(), delay)
  console.warn('🛫 retry sending operations in', Math.round(delay / 1000), 's')
}
const resetSendQueueRetryDelay = () => {
  sendQueueFailureCount = 0
  sendQueueRetryAt = 0
  clearTimeout(sendQueueRetryTimer)
}

const filterOperationsWithinRetryCount = (operations) => {
  return operations.filter(operation => {
    operation.retryCount = operation.retryCount || 0
    operation.retryCount += 1
    const isWithinRetryCount = operation.retryCount <= maxOperationRetryCount
    if (!isWithinRetryCount) {
      console.error('🚒 operation exceeded retry count, it will not be sent again', operation)
    }
    return isWithinRetryCount
  })
}

const isNonCriticalOperationError = (error) => {
  if (nonCriticalOperationErrorStatusCodes.includes(error.status)) { return true }
  // sequelize validation errors, like a unique violation from re-sending an operation that was already saved, nest their status
  const nestedStatus = error.errors?.[0]?.status
  return nonCriticalOperationErrorStatusCodes.includes(nestedStatus)
}

// checks that body name is most recent nameUpdatedAt across queues
const normalizeCardName = (body) => {
  if (!Object.hasOwn(body, 'name')) { return }
  if (!body.nameUpdatedAt) { return }
  const newTime = new Date(body.nameUpdatedAt).getTime()
  const prevTime = cardNameUpdatedAtByCardId[body.id] || 0
  if (newTime < prevTime) {
    delete body.name
    delete body.nameUpdatedAt
  } else {
    cardNameUpdatedAtByCardId[body.id] = newTime
  }
}

let otherItemsQueue
const clearOtherItemsQueue = () => {
  otherItemsQueue = {
    cardIds: [],
    spaceIds: [],
    invites: []
  }
}
clearOtherItemsQueue()

// request handlers

const updateServerUnresponsive = (value) => {
  const globalStore = useGlobalStore()
  globalStore.notifyServerUnresponsive = value
  globalStore.updateIsOnline(!value)
}
const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController()
  const { signal } = controller
  const timeout = setTimeout(() => {
    controller.abort()
  }, consts.requestTimeout)
  try {
    const response = await fetch(url, { ...options, signal })
    clearTimeout(timeout)
    updateServerUnresponsive(false)
    return response
  } catch (error) {
    clearTimeout(timeout)
    console.error('🚒 fetchWithTimeout', url, error)
    if (error.name === 'AbortError') { // timeout
      updateServerUnresponsive(true)
    }
    throw error
  }
}
const shouldRequest = ({ shouldRequestRemote, apiKey, isOnline }) => {
  if (utils.isUndefinedOrNull(isOnline)) {
    isOnline = true
  }
  const currentUserIsSignedIn = Boolean(apiKey)
  if (isOnline && shouldRequestRemote) {
    return true
  }
  if (isOnline && currentUserIsSignedIn) {
    return true
  }
}
const normalizeResponse = async (response) => {
  const success = [200, 201, 202, 204]
  if (success.includes(response.status)) {
    const data = await response.json()
    return data
  } else {
    throw { response, status: response.status }
  }
}

// normalize data

const normalizeRemovedCards = (space) => {
  if (!space.removedCards) { return space }
  space.removedCards.forEach(card => {
    card.isRemoved = true
    space.cards.push(card)
  })
  return space
}
const normalizeCollaboratorKey = (space) => {
  if (!space.collaboratorKey) { return }
  if (typeof space.collaboratorKey === 'string') {
    return space.collaboratorKey
  } else {
    return space.collaboratorKey.collaboratorKey
  }
}

export const useApiStore = defineStore('api', {
  actions: {
    handleServerError ({ name, error, shouldNotNotifyUser }) {
      const globalStore = useGlobalStore()
      console.error('🚒 handleServerError', name, error)
      if (!shouldNotNotifyUser) { return }
      globalStore.updateNotifyConnectionError(true)
      globalStore.updateNotifyConnectionErrorName(name)
    },

    // adds auth credentials to fetch options
    requestOptions (options) {
      const userStore = useUserStore()
      const spaceStore = useSpaceStore()
      const headers = new Headers({
        'Content-Type': 'application/json',
        'Cache-Control': 'must-revalidate, no-store, no-cache, private'
      })
      const space = options.space || spaceStore.getSpaceAllState
      const collaboratorKey = normalizeCollaboratorKey(space)
      const apiKey = userStore.apiKey
      if (collaboratorKey) {
        headers.append('Space-Authorization', collaboratorKey)
      }
      if (apiKey) {
        headers.append('Authorization', apiKey)
      }
      if (options.spaceReadOnlyKey) {
        headers.append('Read-Only-Authorization', options.spaceReadOnlyKey)
      }
      const requestId = options.requestId || nanoid()
      headers.append('Request-Id', requestId)
      headers.append('User-Id', userStore.id)
      return {
        method: options.method,
        headers,
        body: JSON.stringify(options.body)
      }
    },

    // returns the operations to send again
    async handleServerOperationsError ({ error, response, queue }) {
      const globalStore = useGlobalStore()
      // rate limited, send all operations again after waiting
      if (response?.status === 429) {
        console.warn('🚑 handleServerOperationsError rate limited', queue)
        return queue
      }
      // network error or server unavailable, send all operations again
      if (!response || response.status >= 500) {
        console.error('🚒 handleServerOperationsError', error, response, queue)
        const isNetworkError = error instanceof TypeError
        let isServerOnline = true
        // confirm that the server is unreachable before switching to offline mode
        if (isNetworkError) {
          isServerOnline = await this.getStatus()
        }
        if (isServerOnline) {
          globalStore.updateNotifyServerCouldNotSave(true)
        } else {
          globalStore.updateIsOnline(false)
        }
        return queue
      }
      let data = {}
      try {
        data = await response.json()
      } catch (error) {
        console.error('🚒 handleServerOperationsError response', error, response)
      }
      console.warn('🚑 serverOperationsError', data)
      // other request errors, like an invalid or too large request, won't succeed if sent again
      if (response.status !== 422) {
        globalStore.updateNotifyServerCouldNotSave(true)
        return []
      }
      // unexpected error before operations were processed
      const { errors } = data
      if (!errors) {
        globalStore.updateNotifyServerCouldNotSave(true)
        return filterOperationsWithinRetryCount(queue)
      }
      const createdSpaceIds = queue.filter(operation => operation.name === 'createSpace').map(operation => operation.body.id)
      const retryOperationIds = []
      errors.forEach(error => {
        const operation = queue.find(operation => operation.body.operationId === error.operationId)
        const isCritical = !isNonCriticalOperationError(error)
        // operations in a space created in the same request can fail if the space isn't ready yet
        let isInCreatedSpace
        if (operation && operation.name !== 'createSpace') {
          const isUnauthorizedOrNotFound = [401, 404].includes(error.status)
          isInCreatedSpace = isUnauthorizedOrNotFound && createdSpaceIds.includes(operation.body.spaceId)
        }
        if (isCritical) {
          console.error('🚒 critical serverOperationsError operation', error)
          globalStore.updateNotifyServerCouldNotSave(true)
        } else {
          console.warn('🚑 non-critical serverOperationsError operation', error)
        }
        if (isCritical || isInCreatedSpace) {
          retryOperationIds.push(error.operationId)
        }
      })
      const retryOperations = queue.filter(operation => retryOperationIds.includes(operation.body.operationId))
      return filterOperationsWithinRetryCount(retryOperations)
    },

    // Add and Send Queue Operations

    scheduleSendQueue () {
      sendQueueDebounced(this)
    },
    async flushQueue () {
      // send queued operations now, after any that are already sending, even if waiting to retry a failed send
      sendQueueDebounced.cancel()
      await this.trySendQueue({ shouldIgnoreRetryDelay: true })
    },
    async trySendQueue (options = {}) {
      // send one request at a time, so that requeued operations are sent before newer ones
      sendingQueuePromise = sendingQueuePromise
        .catch(error => console.error('🚒 trySendQueue', error))
        .then(() => this.sendQueueOperations(options))
      await sendingQueuePromise
    },
    async sendQueueOperations ({ shouldIgnoreRetryDelay } = {}) {
      const userStore = useUserStore()
      const spaceStore = useSpaceStore()
      const globalStore = useGlobalStore()
      const broadcastStore = useBroadcastStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      const queue = utils.clone(sessionQueue)
      await cache.saveQueue(queue)
      if (!shouldRequest({ apiKey, isOnline }) || !queue.length) { return } // offline check
      // after a failed send, the retry timer sends the queue
      const isWaitingToRetry = Date.now() < sendQueueRetryAt
      if (isWaitingToRetry && !shouldIgnoreRetryDelay) { return }
      if (!spaceStore.id) {
        console.warn('🚑 sendQueue: operation missing spaceId', queue)
        return
      }
      // empty queue into sendingQueue
      globalStore.sendingQueue = queue
      // clear queue before sending
      sessionQueue = []
      cache.clearQueue()
      // send
      let response
      let retryQueue = []
      try {
        const requestId = nanoid()
        console.warn('🛫 sending operations', queue, `●requestId=${requestId}`)
        const options = await this.requestOptions({ body: queue, method: 'POST', requestId })
        response = await fetch(`${consts.apiHost()}/operations`, options)
        if (response.ok) {
          console.info('🛬 operations ok', queue)
        } else {
          throw response.statusText
        }
        if (globalStore.notifyServerCouldNotSave) {
          globalStore.addNotification({ message: 'Reconnected to server', type: 'success' })
          globalStore.notifyServerCouldNotSave = false
        }
      } catch (error) {
        console.error('🚑 sendQueue', error)
        retryQueue = await this.handleServerOperationsError({ error, response, queue })
      }
      if (retryQueue.length) {
        await requeueOperations(retryQueue)
        updateSendQueueRetryDelay(this, response)
      } else {
        // remove sent operations from backup, keeping operations added while sending
        await cache.saveQueueBackup(sessionQueue)
        resetSendQueueRetryDelay()
      }
      globalStore.clearSendingQueue()
      // join broadcast for newly created space
      const isCurrentSpaceCreated = queue.some(operation => operation.name === 'createSpace' && operation.body.id === spaceStore.id)
      if (response && isCurrentSpaceCreated) {
        broadcastStore.joinSpaceRoom({ shouldRejoin: true })
      }
    },

    getQueuedCreateSpaceIds () {
      const globalStore = useGlobalStore()
      const queue = globalStore.sendingQueue.concat(sessionQueue)
      const operations = queue.filter(operation => operation.name === 'createSpace')
      return operations.map(operation => operation.body.id)
    },

    async addToQueue ({ name, body, spaceId, allowNonMember }) {
      const userStore = useUserStore()
      const spaceStore = useSpaceStore()
      const globalStore = useGlobalStore()
      if (!allowNonMember && !userStore.getUserCanEditSpace) { return }
      body = utils.clone(body)
      body.operationId = nanoid()
      body.spaceId = spaceId || spaceStore.id
      body.userId = userStore.id
      body.clientCreatedAt = new Date()
      const isSignedIn = userStore.getUserIsSignedIn
      if (!isSignedIn) { return }
      if (name === 'updateCard') { normalizeCardName(body) }
      const newItem = {
        name,
        body
      }
      sessionQueue = addItemToQueue(sessionQueue, newItem)
      // backup includes operations that are sending, in case they fail
      const sendingQueue = utils.clone(globalStore.sendingQueue)
      cache.saveQueueBackup(sendingQueue.concat(sessionQueue))
      // send to API after debounce period
      this.scheduleSendQueue()
    },

    // Meta

    async getStatus () {
      try {
        const response = await fetchWithTimeout(`${consts.apiHost()}/`)
        return normalizeResponse(response)
      } catch (error) {
        console.info('🚒 getStatus', error)
        return false
      }
    },
    async getDate () {
      try {
        const response = await fetch(`${consts.apiHost()}/meta/date`)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getDate', error })
      }
    },
    async getChangelog () {
      const globalStore = useGlobalStore()
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ shouldRequestRemote: true, isOnline })) { return }
      if (!globalStore.isSpacePage) { return }
      try {
        const response = await fetch(`${consts.apiHost()}/meta/changelog`)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getChangelog', error })
      }
    },
    async updateDateImage () {
      const globalStore = useGlobalStore()
      try {
        const response = await fetch(`${consts.apiHost()}/space/date-image`)
        const data = await normalizeResponse(response)
        globalStore.dateImageUrl = data.url
        return data.url
      } catch (error) {
        this.handleServerError({ name: 'updateDateImage', error })
      }
    },
    async getEmojis () {
      try {
        const response = await fetch(`${consts.apiHost()}/meta/emojis`)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getEmojis', error })
      }
    },
    async kinopioBlogNewPostSubscribe (email) {
      try {
        const body = { email }
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/kinopio-blog/new-post/subscribe`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'subscribeToKinopioBlog', error })
      }
    },

    // Session Token (sign up spam mitigation)

    async createSessionToken (token) {
      const body = { token }
      const options = await this.requestOptions({ body, method: 'POST' })
      return fetch(`${consts.apiHost()}/session-token/create`, options)
    },

    // Sign Up or In

    async signUp ({ email, password, currentUser, sessionToken }) {
      const body = currentUser
      body.email = email
      body.password = password
      body.sessionToken = sessionToken
      const options = await this.requestOptions({ body, method: 'POST' })
      return fetch(`${consts.apiHost()}/user/sign-up`, options)
    },
    async signIn ({ email, password }) {
      const body = {
        email,
        password
      }
      const options = await this.requestOptions({ body, method: 'POST' })
      return fetch(`${consts.apiHost()}/user/sign-in`, options)
    },
    async sendResetPasswordEmail (email) {
      const body = { email }
      const options = await this.requestOptions({ body, method: 'POST' })
      return fetch(`${consts.apiHost()}/user/send-reset-password-email`, options)
    },
    async updatePassword ({ password, apiKey }) {
      const body = { password, apiKey }
      const options = await this.requestOptions({ body, method: 'PATCH', apiKey })
      return fetch(`${consts.apiHost()}/user/update-password`, options)
    },
    async resetPassword ({ password, passwordResetToken }) {
      const body = { password, passwordResetToken }
      const options = await this.requestOptions({ body, method: 'PATCH' })
      return fetch(`${consts.apiHost()}/user/reset-password`, options)
    },
    async updateEmail (email) {
      const body = { email }
      const options = await this.requestOptions({ body, method: 'PATCH' })
      return fetch(`${consts.apiHost()}/user/update-email`, options)
    },

    // User

    async getUser () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/user`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUser', error })
      }
    },
    async getUserTodos () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/user/todos`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUser', error })
      }
    },
    async getUserAtUserMentions () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/user/at-user-mentions`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUser', error })
      }
    },
    async getUserAtDateMentions () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/user/at-date-mentions`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUser', error })
      }
    },
    async getUserFavoriteSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/favorite-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserFavoriteSpaces', error })
      }
    },
    async getUserFavoriteUsers () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/favorite-users`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserFavoriteUsers', error })
      }
    },
    async getUserFavoriteColors () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/favorite-colors`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserFavoriteUsers', error })
      }
    },
    async getUserHiddenSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/hidden-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserHiddenSpaces', error })
      }
    },
    async getFollowingUsersSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        console.info('🛬 getting following users spaces')
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/favorite-users-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getFollowingUsersSpaces', error })
      }
    },
    async getUserSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/spaces`, options)
        const currentUser = userStore
        const spaces = await normalizeResponse(response)
        return utils.addCurrentUserIsCollaboratorToSpaces(spaces, currentUser)
      } catch (error) {
        this.handleServerError({ name: 'getUserSpaces', error })
      }
    },
    async getUserTemplateSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/template-spaces`, options)
        const currentUser = userStore
        const spaces = await normalizeResponse(response)
        return utils.addCurrentUserIsCollaboratorToSpaces(spaces, currentUser)
      } catch (error) {
        this.handleServerError({ name: 'getUserTemplateSpaces', error })
      }
    },
    async getUserGroupSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const groupStore = useGroupStore()
      const groups = groupStore.getCurrentUserGroups
      if (!groups.length) { return }
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/group-spaces`, options)
        const currentUser = userStore
        const spaces = await normalizeResponse(response)
        return utils.addCurrentUserIsCollaboratorToSpaces(spaces, currentUser)
      } catch (error) {
        this.handleServerError({ name: 'getUserGroupSpaces', error })
      }
    },
    async getUserRemovedSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/removed-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserRemovedSpaces', error })
      }
    },
    async getUserInboxSpace () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/inbox-space`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserInboxSpace', error })
      }
    },
    async getSpacesNotificationUnsubscribed () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/spaces-notification-unsubscribed`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpacesNotificationUnsubscribed', error })
      }
    },
    async getGroupsNotificationUnsubscribed () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/groups-notification-unsubscribed`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getGroupsNotificationUnsubscribed', error })
      }
    },
    async spaceNotificationResubscribe (space) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const user = userStore
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/space/${space.id}/notification-resubscribe?userId=${user.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'spaceNotificationResubscribe', error })
      }
    },
    async groupNotificationResubscribe (group) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const user = userStore
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/group/${group.id}/notification-resubscribe?userId=${user.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'groupNotificationResubscribe', error })
      }
    },
    async deleteUserPermanent () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'DELETE' })
        const response = await fetch(`${consts.apiHost()}/user/permanent`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'deleteUserPermanent', error })
      }
    },
    async getPublicUser (user) {
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/public/${user.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getPublicUser', error })
      }
    },
    async getPublicUsers (userIds) {
      const max = 60
      try {
        userIds = userIds.slice(0, max)
        userIds = userIds.join(',')
        if (!userIds) { return }
        console.info('🛬🛬 getting remote public users', userIds)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/user/public/multiple?userIds=${userIds}`, options)
        // notifyConnectionError
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getPublicUsers', error })
      }
    },
    async getPublicUserExploreSpaces (user) {
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/public/explore-spaces/${user.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getPublicUserExploreSpaces', error })
      }
    },
    async updateUserFavorites (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/user/favorites`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'updateUserFavorites', error, shouldNotNotifyUser: true })
      }
    },

    // Space

    async getExploreSpaces () {
      const globalStore = useGlobalStore()
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ shouldRequestRemote: true, isOnline })) { return }
      try {
        console.info('🛬 getting explore spaces')
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/explore-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getExploreSpaces', error })
      }
    },

    async getEveryoneSpaces () {
      const globalStore = useGlobalStore()
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ shouldRequestRemote: true, isOnline })) { return }
      try {
        console.info('🛬 getting everyone spaces')
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/everyone-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getEveryoneSpaces', error })
      }
    },
    async getLiveSpaces () {
      const globalStore = useGlobalStore()
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ shouldRequestRemote: true, isOnline })) { return }
      try {
        console.info('🛬 getting live spaces')
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/live-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getLiveSpaces', error })
      }
    },
    async getSpace ({ space, shouldRequestRemote, spaceReadOnlyKey }) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const spaceStore = useSpaceStore()
      try {
        const apiKey = userStore.apiKey
        const isOnline = globalStore.isOnline
        if (!shouldRequest({ shouldRequestRemote, apiKey, isOnline })) { return }
        const spaceReadOnlyKey = spaceStore.getSpaceReadOnlyKey(space)
        console.info('🛬 getting remote space', space.id)
        const options = await this.requestOptions({ method: 'GET', space, spaceReadOnlyKey })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/${space.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpace', error })
      }
    },
    async getSpaceUpdatedAt (space) {
      const globalStore = useGlobalStore()
      try {
        const isOnline = globalStore.isOnline
        if (!isOnline) { return }
        // console.info('🛬 getting remote space updatedAt', space.id)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/updated-at/${space.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpace', error })
      }
    },
    async getSpaceFavorites () {
      const globalStore = useGlobalStore()
      const spaceStore = useSpaceStore()
      try {
        const isOnline = globalStore.isOnline
        if (!isOnline) { return }
        const spaceId = spaceStore.id
        console.info('🛬 getting remote space favorites', spaceId)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/${spaceId}/favorites`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpaceFavorites', error })
      }
    },
    async getSpaceHistory () {
      const globalStore = useGlobalStore()
      const spaceStore = useSpaceStore()
      try {
        const isOnline = globalStore.isOnline
        if (!isOnline) { return }
        const spaceId = spaceStore.id
        console.info('🛬 getting remote space history', spaceId)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/${spaceId}/history`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpaceFavorites', error })
      }
    },
    async getSpaceAnonymously (space) {
      const globalStore = useGlobalStore()
      const spaceStore = useSpaceStore()
      const isOnline = globalStore.isOnline
      if (!isOnline) { return }
      const invitedSpaces = await cache.invitedSpaces()
      const invite = invitedSpaces.find(invitedSpace => invitedSpace.id === space.id) || {}
      space.collaboratorKey = space.collaboratorKey || invite.collaboratorKey
      const spaceReadOnlyKey = spaceStore.getSpaceReadOnlyKey(space)
      try {
        console.info('🛬 getting remote space anonymously', space.id, space.collaboratorKey, spaceReadOnlyKey)
        const options = await this.requestOptions({ method: 'GET', space, spaceReadOnlyKey })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/${space.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpaceAnonymously', error })
      }
    },
    async getInboxSpace () {
      try {
        console.info('🛬 getting inbox space')
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/space/inbox`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getInboxSpace', error })
      }
    },
    async searchExploreSpaces (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/space/search-explore-spaces`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'search', error })
      }
    },
    async getPublicSpaces (spaceIds, shouldUseProduction) {
      try {
        spaceIds = spaceIds.join(',')
        console.info('🛬 getting remote public spaces', spaceIds)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost(shouldUseProduction)}/space/public/multiple?spaceIds=${spaceIds}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getPublicSpaces', error })
      }
    },
    async createSpaces () {
      const userStore = useUserStore()
      try {
        let spaces = await cache.getAllSpaces()
        if (!spaces.length) { return }
        const drawingImages = []
        spaces = spaces.map(space => {
          space = normalizeRemovedCards(space)
          if (space.drawingImage) {
            drawingImages.push({
              spaceId: space.id,
              dataUrl: space.drawingImage
            })
            delete space.drawingImage
          }
          return space
        })
        let removedSpaces = await cache.getAllRemovedSpaces()
        removedSpaces = removedSpaces.map(space => {
          space.isRemoved = true
          space.removedByUserId = userStore.id
          delete space.drawingImage
          return space
        })
        removedSpaces.forEach(space => spaces.push(space))
        spaces = spaces.filter(space => space)
        const options = await this.requestOptions({ body: spaces, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/space/multiple`, options)
        // upload drawing images
        for (const body of drawingImages) {
          const isDataUrl = body.dataUrl.startsWith('data:')
          if (!isDataUrl) { continue }
          try {
            const imageOptions = await this.requestOptions({ body, method: 'POST', space: { id: body.spaceId } })
            const imageResponse = await fetch(`${consts.apiHost()}/space/drawing-image`, imageOptions)
            const imageData = await imageResponse.json()
          } catch (error) {
            console.error('🚒 createSpaces', error, body.dataUrl)
          }
        }
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createSpaces', error })
      }
    },
    async createSpace (space) {
      try {
        space = normalizeRemovedCards(space)
        const body = space
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/space`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createSpace', error })
      }
    },
    async updateSpacePreviewImage (spaceId) {
      const spaceStore = useSpaceStore()
      const themeStore = useThemeStore()
      try {
        spaceId = spaceId || spaceStore.id
        const themeOptions = themeStore.previewImageThemeOptions()
        const body = { spaceId, themeOptions }
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/space/preview-image`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'updateSpacePreviewImage', error, shouldNotNotifyUser: false })
      }
    },
    async updateSpace (space) {
      try {
        const body = space
        const options = await this.requestOptions({ body, method: 'PATCH', space })
        const response = await fetch(`${consts.apiHost()}/space`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'updateSpace', error })
      }
    },
    async getSpaceRemovedCards (space) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/space/${space.id}/removed-cards`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpaceRemovedCards', error })
      }
    },
    async getSpaceCollaboratorKey (space) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/space/${space.id}/collaborator-key`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getSpaceCollaboratorKey', error })
      }
    },
    async addSpaceCollaborator ({ spaceId, collaboratorKey }) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      const userId = userStore.id
      try {
        const body = { userId, spaceId, collaboratorKey }
        const space = { id: spaceId, collaboratorKey }
        const options = await this.requestOptions({ body, method: 'PATCH', space })
        const response = await fetch(`${consts.apiHost()}/space/collaborator`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'addSpaceCollaborator', error })
      }
    },
    async removeSpaceCollaborator ({ space, user }) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const spaceStore = useSpaceStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const body = {
          spaceId: space.id,
          userId: user.id
        }
        const options = await this.requestOptions({ body, method: 'DELETE' })
        const response = await fetch(`${consts.apiHost()}/space/collaborator`, options)
        const data = await normalizeResponse(response)
        // TODO user should explicitly rotate collaborator key
        // const collaboratorKey = data.collaboratorKey
        // spaceStore.updateSpace({ collaboratorKey })
      } catch (error) {
        this.handleServerError({ name: 'removeSpaceCollaborator', error })
      }
    },
    async restoreRemovedSpace (space) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/space/restore/${space.id}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'restoreRemovedSpace', error })
      }
    },
    async sendSpaceInviteEmails (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/space/email-invites/${body.spaceId}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'sendSpaceInviteEmails', error })
      }
    },

    // Other Items Queue

    async addToGetOtherItemsQueue ({ cardIds, spaceIds, invites }) {
      const globalStore = useGlobalStore()
      const isOnline = globalStore.isOnline
      if (!isOnline) { return }
      otherItemsQueue = {
        cardIds: uniq(otherItemsQueue.cardIds.concat(cardIds)),
        spaceIds: uniq(otherItemsQueue.spaceIds.concat(spaceIds)),
        invites: uniq(otherItemsQueue.invites.concat(invites))
      }
      this.debouncedSendOtherItemsQueue()
    },

    debouncedSendOtherItemsQueue: debounce(async function () {
      this.sendOtherItemsQueue()
    }, 500),

    // Get Other Items Queue

    async sendOtherItemsQueue () {
      const globalStore = useGlobalStore()
      const { cardIds, spaceIds, invites } = otherItemsQueue
      const isEmpty = !cardIds.length && !spaceIds.length && !invites.length
      if (isEmpty) { return }
      globalStore.isLoadingOtherItems = true
      clearOtherItemsQueue()
      const body = { cardIds, spaceIds, invites }
      try {
        console.info('🛬🛬 getting remote other items', { cardIds, spaceIds, invites })
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/item/multiple`, options)
        const data = await normalizeResponse(response)
        globalStore.updateOtherItems(data)
      } catch (error) {
        this.handleServerError({ name: 'sendOtherItemsQueue', error })
      }
      globalStore.isLoadingOtherItems = false
    },

    // Card

    async getCardsWithLinkToSpaceId (spaceId) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/card/by-link-to-space/${spaceId}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getCardsWithLinkToSpaceId', error })
      }
    },
    async getCardHistory (card) {
      const globalStore = useGlobalStore()
      try {
        const isOnline = globalStore.isOnline
        if (!isOnline) { return }
        console.info('🛬 getting remote card history', card.id)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetchWithTimeout(`${consts.apiHost()}/card/${card.id}/history`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getCardHistory', error })
      }
    },
    async updateCards (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/card/multiple`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'updateCards', error })
      }
    },
    async createCards (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/card/multiple`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createCards', error })
      }
    },
    async createCard (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/card`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createCard', error })
      }
    },
    async createCardInInbox (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/card/to-inbox`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createCardInInbox', error })
      }
    },
    async searchCards (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/card/search`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'search', error })
      }
    },
    async updateCardVote (body) {
      const spaceStore = useSpaceStore()
      try {
        const space = spaceStore.getSpaceAllState
        const spaceReadOnlyKey = spaceStore.getSpaceReadOnlyKey(space) // allows read only users to vote
        const options = await this.requestOptions({ body, method: 'PATCH', space, spaceReadOnlyKey })
        const response = await fetch(`${consts.apiHost()}/card/update-counter`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'update-counter', error })
      }
    },
    async updateUrlPreviewImage (body) {
      try {
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/card/update-url-preview-image`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'update-counter', error })
      }
    },

    // Connection

    async updateConnections (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/connection/multiple`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'updateConnections', error })
      }
    },
    async createConnections (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/connection/multiple`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createConnections', error })
      }
    },

    // Boxes

    async createBoxes (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/box/multiple`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createBoxes', error })
      }
    },
    async updateBox (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/box`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'updateBoxes', error })
      }
    },

    // Tag

    async getCardsWithTag (name) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      name = encodeURI(name)
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/card/by-tag-name/${name}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getCardsWithTag', error })
      }
    },
    async getUserTags (removeUnusedTags) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return [] }
      try {
        let params = ''
        if (removeUnusedTags) {
          params = '?removeUnusedTags=true'
        }
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/tags${params}`, options)
        return normalizeResponse(response) || []
      } catch (error) {
        this.handleServerError({ name: 'getUserTags', error })
      }
    },
    // async updateUserTagsColor (tag) {
    // const apiKey = userStore.apiKey
    //   if (!shouldRequest({apiKey})) { return }
    //   try {
    //     const options = await this.requestOptions({ method: 'PATCH', tag })
    //     const response = await fetch(`${consts.apiHost()}/tags/color`, options)
    //     return normalizeResponse(response)
    //   } catch (error) {
    //     console.error('🚒 updateUserTagsColor', error)
    //   }
    // },

    // Billing Stripe

    // for lifetime plan
    async checkoutUrl (body) {
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/billing/stripe/checkout-url`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'subscriptionUrl', error })
      }
    },
    // for subscription plans
    async subscriptionUrl (body) {
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/billing/stripe/subscription-url`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'subscriptionUrl', error })
      }
    },
    async customerPortalUrl (body) {
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/billing/stripe/customer-portal-url`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'customerPortalUrl', error })
      }
    },
    async donationUrl (body) {
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/billing/stripe/donation-url`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'donationUrl', error })
      }
    },

    // Upload

    async createPresignedPost (body) {
      const spaceStore = useSpaceStore()
      try {
        body.requestSpaceId = spaceStore.id
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/upload/presigned-post`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createPresignedPost', error })
      }
    },
    async createMultiplePresignedPosts (body) {
      const spaceStore = useSpaceStore()
      try {
        body.requestSpaceId = spaceStore.id
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/upload/presigned-post/multiple`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createMultiplePresignedPosts', error })
      }
    },

    // Notifications

    async getNotifications () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/notification`, options)
        const notifications = await normalizeResponse(response)
        return notifications
      } catch (error) {
        this.handleServerError({ name: 'getNotifications', error })
      }
    },
    async deleteAllNotifications () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'DELETE' })
        const response = await fetch(`${consts.apiHost()}/notification/all`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'deleteGroupPermanent', error })
      }
    },

    // Services

    async updateArenaAccessToken (arenaReturnedCode) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      try {
        const currentUserIsSignedIn = userStore.getUserIsSignedIn
        let userId
        if (currentUserIsSignedIn) {
          userId = userStore.id
        }
        const body = {
          userId,
          arenaReturnedCode
        }
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/user/update-arena-access-token`, options)
        return normalizeResponse(response)
      } catch (error) {
        console.error('🚒 updateArenaAccessToken', error)
        globalStore.triggerArenaAuthenticationError()
        globalStore.isAuthenticatingWithArena = false
      }
    },
    async urlPreview ({ url, card }) {
      try {
        const body = { url, card }
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/services/url-preview`, options)
        if (response.status !== 200) {
          throw new Error(response.status)
        }
        const data = await normalizeResponse(response)
        return { url, data, response }
      } catch (error) {
        this.handleServerError({ name: 'urlPreview', error })
      }
    },
    async imageSearch (search) {
      try {
        const body = { search }
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/services/image-search`, options)
        const data = await normalizeResponse(response)
        return data
      } catch (error) {
        this.handleServerError({ name: 'imageSearch', error })
      }
    },
    async gifImageSearch (body) {
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/services/gif-image-search`, options)
        const data = await normalizeResponse(response)
        return data
      } catch (error) {
        this.handleServerError({ name: 'imageSearch', error })
      }
    },
    async communityBackgrounds () {
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/services/community-backgrounds`, options)
        const data = await normalizeResponse(response)
        return data
      } catch (error) {
        console.error('🚒 communityBackgrounds', error)
      }
    },
    async pdf () {
      const spaceStore = useSpaceStore()
      const themeStore = useThemeStore()
      try {
        const body = {
          spaceId: spaceStore.id,
          isDarkTheme: themeStore.getIsThemeDark
        }
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/services/pdf`, options)
        let url = await normalizeResponse(response)
        url = url.url
        return url
      } catch (error) {
        this.handleServerError({ name: 'pdf', error })
      }
    },

    // Downloads

    async downloadAllSpaces () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/space/download-all`, options)
        return response.blob()
      } catch (error) {
        this.handleServerError({ name: 'downloadAllSpaces', error })
      }
    },

    // Group

    async getUserGroups () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        globalStore.isLoadingGroups = true
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/user/groups`, options)
        globalStore.isLoadingGroups = false
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getUserGroups', error })
      }
      globalStore.isLoadingGroups = false
    },
    async getGroup (groupId) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        console.info('🛬 getting remote group', groupId)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/group/${groupId}`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getGroup', error })
      }
    },
    async getGroupPublicMeta (groupId) {
      try {
        console.info('🛬 getting remote group public meta', groupId)
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/group/${groupId}/public-meta`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getGroupPublicMeta', error })
      }
    },
    async createGroup (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/group/`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createGroup', error })
      }
    },
    async createGroupUser (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/group/group-user`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createGroupUser', error })
      }
    },
    async removeGroupUser (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'DELETE' })
        const response = await fetch(`${consts.apiHost()}/group/group-user`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'removeGroupUser', error })
      }
    },
    async deleteGroupPermanent (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'DELETE' })
        const response = await fetch(`${consts.apiHost()}/group/`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'deleteGroupPermanent', error })
      }
    },

    // Analytics

    async sendAnalyticsEvent (body) {
      try {
        const headers = new Headers({
          'Content-Type': 'application/json'
        })
        const response = await fetch(`${consts.apiHost()}/services/analytics-event`, {
          headers,
          method: 'POST',
          body: JSON.stringify(body)
        })
      } catch (error) {
        console.error('🚒 sendAnalyticsEvent', error)
      }
    },

    // Moderator

    async moderatorRestartServer () {
      try {
        const options = await this.requestOptions({ method: 'POST' })
        const response = await fetch(`${consts.helperServerHost()}/moderator/restart-server`, options)
        return normalizeResponse(response)
      } catch (error) {
        console.error('🚒 moderatorRestartServer', error)
        throw new Error(error)
      }
    },
    async applyStudentDiscount (email) {
      try {
        const body = { email }
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/user/apply-student-discount`, options)
        return normalizeResponse(response)
      } catch (error) {
        console.error('🚒 applyStudentDiscount', error)
        throw new Error(error)
      }
    },

    // Affiliates

    async getAffiliate () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        console.info('🛬 getting affiliate')
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/affiliate`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getAffiliate', error })
      }
    },

    // App API Keys

    async getAppApiKeys () {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ method: 'GET' })
        const response = await fetch(`${consts.apiHost()}/app-api-key`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'getAppApiKeys', error })
      }
    },
    async createAppApiKey (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'POST' })
        const response = await fetch(`${consts.apiHost()}/app-api-key`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'createAppApiKey', error })
      }
    },
    async rotateAppApiKey (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'PATCH' })
        const response = await fetch(`${consts.apiHost()}/app-api-key/rotate-key`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'rotateAppApiKey', error })
      }
    },
    async deleteAppApiKey (body) {
      const globalStore = useGlobalStore()
      const userStore = useUserStore()
      const apiKey = userStore.apiKey
      const isOnline = globalStore.isOnline
      if (!shouldRequest({ apiKey, isOnline })) { return }
      try {
        const options = await this.requestOptions({ body, method: 'DELETE' })
        const response = await fetch(`${consts.apiHost()}/app-api-key`, options)
        return normalizeResponse(response)
      } catch (error) {
        this.handleServerError({ name: 'deleteGroupPermanent', error })
      }
    }

  }
})
